import { ExternalLink, FileText } from 'lucide-react';

type Props = {
  url: string;
  mimeType: string | null;
  fileName: string | null;
};

/**
 * Lightweight in-browser viewer for the most common document mimes.
 * Falls back to a "download" affordance for everything else.
 */
export function FileViewer({ url, mimeType, fileName }: Props) {
  if (!mimeType) return <DownloadFallback url={url} fileName={fileName} />;

  if (mimeType === 'application/pdf') {
    return (
      <embed
        src={`${url}#toolbar=1&navpanes=0`}
        type="application/pdf"
        className="h-full w-full"
      />
    );
  }

  if (mimeType.startsWith('image/')) {
    return (
      <img
        src={url}
        alt={fileName ?? 'document'}
        className="mx-auto max-h-full max-w-full object-contain"
      />
    );
  }

  return <DownloadFallback url={url} fileName={fileName} />;
}

function DownloadFallback({ url, fileName }: { url: string; fileName: string | null }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
      <FileText className="size-8 text-[var(--color-foreground-muted)]" />
      <div className="text-sm font-medium">{fileName ?? 'File'}</div>
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1.5 text-sm text-[var(--color-primary)] hover:underline"
      >
        Open file <ExternalLink className="size-3.5" />
      </a>
    </div>
  );
}
